<template>
  <control-base
    :config="conf"
    :dataType="dataType"
    :parentName="$options.name"
    @property-selected="addProperty"
    @property-removed="removeProperty"
    @loaded="$emit('loaded', $event)"
    ref="base"
  >
    <template v-slot:filters>
      <div class="flex-full">
        <p>Rows per page:</p>
        <select class="browser-default" v-model="conf.rows">
          <option
            v-for="(option, index) in perPage"
            :key="index"
            :value="option"
            :selected="option == conf.rows"
          >
            {{ option === -1 ? "All" : option }}
          </option>
        </select>
      </div>

      <div
        class="flex-full"
        style="
          align-items: center;
          justify-content: space-between;
          border: 1px solid #dfdfdf;
          padding: 4px;
        "
      >
        <p>When property:</p>
        <drop-down
          style="width: 30%"
          :options="selectableProps"
          :selected="conf.rowHighlight.property"
          placeholder="None"
          @updateOption="selectProperty"
          :closeOnOutsideClick="true"
        />
        <p>Is greater than:</p>
        <input
          v-model="conf.rowHighlight.quantity"
          type="number"
          style="width: 5%"
        />
        <div>
          <p>Change row colour to:</p>
          <colour-picker
            :color="conf.rowHighlight.color"
            v-model="conf.rowHighlight.color"
          />
        </div>
      </div>

      <reorder-array
        title="Column Order"
        :values="conf.columns"
        display="label"
        @order-changed="updateOrder"
      />
    </template>
    <template v-slot:control>
      <DataTableDx
        class="table"
        title="Campaign"
        :columns="cols"
        :data="campaigns"
        table-key="campaignName"
        :page-size="conf.rows"
        @columns-changed="saveCols"
        :contextMenu="addContextMenu"
        :row-prepared="changeRowColor"
        ref="table"
      />
      <popup ref="confirm"></popup>
    </template>
  </control-base>
</template>

<script>
import { DataTableDx } from "@/components";
import ArrayStore from "devextreme/data/array_store";

export default {
  name: "CampaignTable",

  components: {
    DataTableDx,
  },

  props: {
    config: {
      required: true,
      value: Object,
    },

    shouldResize: {
      required: true,
      value: Boolean,
    },
  },

  watch: {
    shouldResize() {
      this.reactiveShouldResize = this.shouldResize;
      this.$nextTick(() => this.$emit("resized"));
    },

    rowHighlight: {
      deep: true,
      handler() {
        this.refreshTable()
      },
    },
  },

  data() {
    return {
      dataType: "campaign",
      perPage: [-1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50],
      reactiveShouldResize: this.shouldResize,
    };
  },

  computed: {
    conf() {
      return this.config;
    },

    rowHighlight() {
      return this.conf.rowHighlight;
    },

    cols() {
      var cols = [...this.conf.columns];
      if (cols.findIndex((x) => x.field == "campaignName") == -1)
        cols.push({
          label: this.addSpaces("campaignName"),
          field: this.fieldCase("campaignName"),
          visible: false,
          width: 0,
          numeric: false,
          html: false,
        });
      return cols;
    },

    selectableProps() {
      return this.conf.columns.map((x) => x.field);
    },

    layoutLocked() {
      return this.$store.getters.locked;
    },

    campaigns() {
      if (Object.keys(this.$store.getters.campaignData).length == 0) {
        return new ArrayStore({
          key: "campaignName",
          data: [],
        });
      }
      return new ArrayStore({
        key: "campaignName",
        data: this.$store.getters.campaignData.filter((val) =>
          this.conf.campaignNames.includes(val.campaignName)
        ),
      });
    },

    validHighlight() {
      return !(
        !this.conf.rowHighlight.property && !this.conf.rowHighlight.quantity
      );
    },
  },

  mounted() {
    this.$emit("resized");
  },

  methods: {
    refreshTable() {
      this.$refs.table.refresh();
    },

    changeRowColor(row) {
      if (this.shouldHighlight(row.data))
        row.rowElement.style.backgroundColor = this.conf.rowHighlight.color;
    },

    shouldHighlight(item) {
      if (!this.validHighlight || !item) return false;
      if (
        !Object.prototype.hasOwnProperty.call(
          item,
          this.conf.rowHighlight.property
        )
      )
        return false;
      if (
        item[this.conf.rowHighlight.property] < this.conf.rowHighlight.quantity
      )
        return false;

      return true;
    },

    addContextMenu(e) {
      if (e.row.rowType === "data") {
        if (!e.items) e.items = [];
        e.items.push(
          ...[
            {
              text: "Start",
              onItemClick: () => {
                this.toggleCampaign(e.row.key, 'start')
              },
            },
            {
              text: "Stop",
              onItemClick: () => {
                this.toggleCampaign(e.row.key, 'stop')
              },
            },
          ]
        );
      }
    },

    addProperty(name) {
      this.conf.columns.push({
        label: this.addSpaces(name),
        field: this.fieldCase(name),
        width: 0,
        numeric: false,
        html: false,
      });
      this.$emit("configChange", this.conf);
    },

    removeProperty(name) {
      var index = this.conf.columns.findIndex(
        (x) => x.field == this.fieldCase(name)
      );
      this.conf.columns.splice(index, 1);
      this.$emit("configChange", this.conf);
    },

    async toggleCampaign(campaignName, state) {
      var confirm = await this.$refs.confirm.show({
        title: "Toggle campaign",
        message: `Are you sure you want to set ${campaignName} to ${state}?`,
        okButton: "Set",
        isConfirm: true,
      });

      if (confirm) {
        this.$diamond.setCampaignState(campaignName, state).then(() => {
          this.$toast(
            "success",
            `Sent command to set ${campaignName} to ${state}.`
          );
        });
      }
    },

    saveCols(data) {
      this.conf.columns = data.map((x) => {
        var index = this.conf.columns.findIndex((z) => z.field == x.field);
        return {
          ...x,
          label: this.conf.columns[index].label,
          color: this.conf.columns[index].color,
        };
      });
      this.$emit("configChange", this.conf);
    },

    updateOrder(cols) {
      this.conf.columns = cols;
      this.$emit("configChange", this.conf);
    },

    updateRowsPerPage(val) {
      this.conf.rows = val;
      this.$emit("configChange", this.conf);
    },

    addSpaces(str) {
      return str.replace(/([A-Z])/g, " $1").trim();
    },

    selectProperty(item) {
      this.conf.rowHighlight.property = item;
      this.$emit("configChange", this.conf);
    },
  },
};
</script>

<style scoped>
.table {
  width: 100%;
  height: 100%;
}
</style>